import { useStyles, Subscription } from '@archipro-design/aria';

import { useFetcher } from '@remix-run/react';
import type { SubmitHandler, FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { SubscriptionAction } from '~/routes/remix-api.user.subscription';

import * as S from './NewsletterSubscriptionForm.style';

const SUCCESS_MESSAGE = `You are now subscribed to Digital Magazines`;

const subscriptionSchema = z.object({
    email: z
        .string()
        .min(1, { message: 'Email address is required' })
        .email('This is not a valid email address'),
});

const NewsletterSubscriptionForm = () => {
    const { css } = useStyles();
    const fetcher = useFetcher<SubscriptionAction>();
    const error =
        fetcher.data && 'error' in fetcher.data && fetcher.data.message;
    const success =
        fetcher.data && 'success' in fetcher.data && SUCCESS_MESSAGE;

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(subscriptionSchema),
        mode: 'onSubmit',
    });

    const { onChange, name, ref } = register('email');
    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        const { email } = data;
        if (email && !errors?.email?.message) {
            fetcher.submit(
                { email: email },
                {
                    method: 'post',
                    action: '/remix-api/user/subscription',
                }
            );
        }
    };

    return (
        <div className={css(S.Container)}>
            <fetcher.Form
                style={{ width: '100%' }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Subscription
                    submit={{
                        children: 'SUBSCRIBE',
                        loading: fetcher.state === 'submitting',
                        size: 16,
                    }}
                    input={{
                        name: name,
                        inputRef: ref,
                        successIndicator: {},
                        onChange: (e) => onChange(e),
                    }}
                    error={errors?.email?.message ?? error}
                    success={{
                        children:
                            !errors?.email?.message && success ? success : '',

                        className: css(S.SuccessMsg),
                    }}
                    className={css(S.Subscription)}
                    variables={(siteVars) => ({
                        subscriptionButtonBackgroundColor:
                            siteVars?.colors.charcoal[250],
                        textBorderBottomColor: siteVars?.colors.charcoal[250],
                        primaryTextColor: siteVars?.colors.charcoal[250],
                        placeholderTextColor: siteVars?.colors.charcoal[250],
                    })}
                ></Subscription>
            </fetcher.Form>
        </div>
    );
};

export default NewsletterSubscriptionForm;
